/**
 * Etiketter, färger och texter för Mina ärenden (BL-377, mina-forslag-komponentspec.md avsnitt 3).
 * Statusen räknas ut på servern; här finns bara hur den visas.
 */

export const STATUS = {
  mottagen: { label: 'Mottagen', farg: 'neutral', text: 'Vi har tagit emot ditt ärende. Du får ett mejl när det har registrerats.' },
  bedoms: { label: 'Under bedömning', farg: 'neutral', text: 'Ärendet bedöms och läggs in i planeringen.' },
  registrerad: { label: 'Registrerad', farg: 'bla', text: 'Ärendet är registrerat i planeringen.' },
  vantar: { label: 'Väntar', farg: 'neutral', text: 'Arbetet väntar på ett beslut eller på annat arbete.' },
  planerad: { label: 'Planerad', farg: 'bla', text: 'Arbetet är planerat.' },
  pagar: { label: 'Pågår', farg: 'gul', text: 'Arbetet pågår. Du får ett mejl när det är klart och i drift.' },
  levererad: { label: 'Levererad', farg: 'gron', text: 'Klart och i drift.' },
  besvarad: { label: 'Besvarad', farg: 'gron', text: '' },
  avvisad: { label: 'Avvisad', farg: 'neutral', text: 'Ärendet tas inte vidare.' },
  avslutad: { label: 'Avslutad', farg: 'neutral', text: 'Ärendet är avslutat.' },
};

const PA_GANG_STATUS = { mottagen: 'registrerad', vantar: 'väntar', planerad: 'planerad', utveckling: 'pågår', test: 'pågår', levererad: 'levererad' };

export const TYPNAMN = { bug: 'Bugg', improvement: 'Förbättring', ny_funktion: 'Ny funktion', question: 'Fråga' };

/** Stegen i stegraden. Väntar markerar Planerad. */
export const STEG = ['Mottagen', 'Registrerad', 'Planerad', 'Pågår', 'Levererad'];
const STEG_INDEX = { mottagen: 0, bedoms: 0, registrerad: 1, vantar: 2, planerad: 2, pagar: 3, levererad: 4 };

/** Etikett, färg och lägestext för en rad från servern. */
export function visning(rad) {
  if (rad.dubblett && rad.bl) {
    const info = rad.pa_gang || {};
    const prio = info.prio ? ` med prio ${info.prio}` : '';
    const status = PA_GANG_STATUS[info.status] || 'registrerad';
    return {
      label: `Samma som ${rad.bl}`,
      farg: 'bla',
      text: `Samma sak finns redan i planeringen som ${rad.bl}${prio} och status ${status}. Ditt ärende följer den posten, och du ser dess status här.`,
    };
  }
  return STATUS[rad.status] || STATUS.mottagen;
}

/** Stegets index, eller null när stegraden inte visas (besvarad, avvisad, avslutad, dubblett). */
export function stegIndex(rad) {
  if (rad.dubblett) return null;
  return Object.prototype.hasOwnProperty.call(STEG_INDEX, rad.status) ? STEG_INDEX[rad.status] : null;
}

/** Ärenden som fortfarande är på väg: räknaren i fliken. */
export function arAktivt(rad) {
  return !rad.dubblett && !['levererad', 'besvarad', 'avvisad', 'avslutad'].includes(rad.status);
}

const MANADER = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/**
 * Servern skickar UTC utan tidszon, och Python med mikrosekunder (sex decimaler), som Safari inte
 * tolkar. Ger en ISO-sträng med högst millisekunder och Z, som alla webbläsare läser.
 */
export function normaliseraTid(iso) {
  const kort = String(iso).replace(/(\.\d{3})\d+/, '$1');
  return /([zZ]|[+-]\d\d:\d\d)$/.test(kort) ? kort : `${kort}Z`;
}

/** Tolka serverns tid som UTC; visas sedan i svensk tid. */
function tolka(iso) {
  if (!iso) return null;
  const d = new Date(normaliseraTid(iso));
  return Number.isNaN(d.getTime()) ? null : d;
}

function delar(d) {
  const f = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return { dag: Number(p.day), man: MANADER[Number(p.month) - 1], tid: `${p.hour}:${p.minute}` };
}

/** "28 okt" */
export function datum(iso) {
  const d = tolka(iso);
  if (!d) return '';
  const p = delar(d);
  return `${p.dag} ${p.man}`;
}

/** "28 okt 14:12", eller bara datum för en dag utan klockslag (leveransdatum). */
export function datumTid(iso) {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [, m, d] = iso.split('-').map(Number);
    return `${d} ${MANADER[m - 1]}`;
  }
  const d = tolka(iso);
  if (!d) return '';
  const p = delar(d);
  return `${p.dag} ${p.man} ${p.tid}`;
}

/* ── Ny status: senast sedd status per ärende, i webbläsaren (ingen databas) ── */
const SEDD_NYCKEL = 'kp-mina-arenden-sedd';

function lasSedd() {
  try {
    const v = JSON.parse(window.localStorage.getItem(SEDD_NYCKEL) || '{}');
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

function skrivSedd(v) {
  try { window.localStorage.setItem(SEDD_NYCKEL, JSON.stringify(v)); } catch { /* privat läge: ingen prick */ }
}

const nyckelFor = (rad) => `${rad.status}|${rad.bl || ''}|${rad.kommentar ? rad.kommentar.tid || 'k' : ''}`;

/**
 * Refs vars status ändrats sedan användaren senast öppnade dem. Ärenden som aldrig setts
 * förut sparas som utgångsläge utan prick, så att första besöket inte lyser upp allt.
 */
export function nyStatus(rader) {
  const sedd = lasSedd();
  const nya = new Set();
  let andrad = false;
  rader.forEach((r) => {
    const k = nyckelFor(r);
    if (!(r.ref in sedd)) { sedd[r.ref] = k; andrad = true; }
    else if (sedd[r.ref] !== k) nya.add(r.ref);
  });
  if (andrad) skrivSedd(sedd);
  return nya;
}

/** Markerar ärendet som sett i nuvarande status. */
export function markeraSett(rad) {
  const sedd = lasSedd();
  sedd[rad.ref] = nyckelFor(rad);
  skrivSedd(sedd);
}
